import { Box, Button, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import styles from './Report.module.scss';
import classNames from 'classnames/bind';
import ReportStats from './components/ReportStats';
import RecentProjects from './components/RecentProjects';
import { ExportFileIcon } from '~/assets/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { adminRouteAbsolute } from '~/utils/constants/route';
import BaseModal from '~/components/common/base-modal/BaseModal';
import { CLASSIC_REPORTS } from './constants/classicReports';
import { DEFAULT_REPORT_VIEW, ReportView, resolveViewFromSearch } from './utils/url';
import reportApi from '~/apis/report';
import { formatCurrency, formatDate } from './utils/format';
import PrimaryButton from '~/components/common/primary-button/PrimaryButton';
import { IInsights, IMyTaskRecord, IInvoiceOpenSalesRepRecord, IPaidAndLeftReviewRecord, IPaymentRecord, IMyPaymentsRecord, IRoofs, IShelbyvilleJob, IShingleColor, IShingle } from '~/utils/interface/report';
import DealOverviewChart from '../dashboard/components/DealOverviewChart';
import dayjs from 'dayjs';

const escapeCsvValue = (value: string): string => {
  if (value === undefined || value === null) {
    return '';
  }

  if (value.includes('"') || value.includes(',') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

const cx = classNames.bind(styles);

const Report = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState<ReportView>(() => resolveViewFromSearch(location.search) ?? DEFAULT_REPORT_VIEW);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState(CLASSIC_REPORTS[0]?.path ?? '');
  const [isExporting, setIsExporting] = useState(false);
  const [insights, setInsights] = useState<IInsights | null>(null);

  useEffect(() => {
    setSelectedReportType(CLASSIC_REPORTS[0]?.path ?? '');
  }, [view]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await reportApi.getInsights();
        setInsights(response.data?.data ?? null);
      } catch (error) {
        console.error('Failed to fetch insights', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const tab = resolveViewFromSearch(location.search);
    if (tab && tab !== view) {
      setView(tab);
    }
  }, [location.search, view]);

  const handleChangeView = useCallback((nextView: ReportView) => {
    setView(nextView);
    navigate(
      {
        pathname: location.pathname,
        search: `?view=${nextView}`,
      },
      { replace: true },
    );
  }, [location.pathname, navigate]);

  const handleOpenExport = useCallback(() => {
    setIsExportModalOpen(true);
  }, []);

  const handleCloseExport = useCallback(() => {
    setIsExportModalOpen(false);
  }, []);

  const handleConfirmExport = useCallback(async () => {
    if (!selectedReportType || isExporting) {
      return;
    }

    const report = CLASSIC_REPORTS.find((r) => r.path === selectedReportType);
    if (!report) {
      return;
    }

    setIsExporting(true);

    try {
      let header: string[] = [];
      let rows: string[][] = [];
      let filename = '';

      switch (selectedReportType) {
        case 'closed-invoices': {
          const response = await reportApi.getClosedInvoices({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = [
            'Sales Rep',
            'Status',
            'Number',
            'Sub Total',
            'Total',
            'Total Paid',
            'Balance',
            'Related Contact',
            'Related Job',
            'Date Updated',
            'Date Invoice',
          ];
          rows = records.map((item) => [
            item.saleRepName || '-',
            item.status || '-',
            item.numberOfInvoices || '-',
            formatCurrency(item.subTotal),
            formatCurrency(item.total),
            formatCurrency(item.totalPaid),
            formatCurrency(item.balance),
            item.relatedContact?.name ?? '-',
            item.jobId ? `Job #${item.jobId}` : '-',
            formatDate(item.dateUpdated),
            formatDate(item.dateInvoiced),
          ]);
          filename = 'closed-invoices.csv';
          break;
        }

        case 'my-tasks': {
          const response = await reportApi.getMyTasks({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = ['Title', 'Type', 'Start Date', 'Due Date', 'Priority', 'Status', 'Assigned To', 'Related Job', 'Customer Name', 'Description'];
          rows = records.map((item: IMyTaskRecord) => [
            item.title || '-',
            item.type || '-',
            item.startDate ? dayjs(item.startDate).format('MM/DD/YYYY hh:mm A') : '-',
            item.dueDate ? dayjs(item.dueDate).format('MM/DD/YYYY hh:mm A') : '-',
            item.priority || '-',
            item.status || '-',
            item.assignee?.name || '-',
            item.job?.uniqueCode || '-',
            item.job?.customerName || '-',
            item.description || '-',
          ]);
          filename = 'my-tasks.csv';
          break;
        }

        case 'invoice-open-sales-rep': {
          const response = await reportApi.getInvoiceOpenSalesRep({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = ['Sales Rep', 'Status', 'Number', 'Due Date', 'Date Invoice', 'Total', 'Total Paid', 'Balance', 'Related Contact', 'Job Code'];
          rows = records.map((item: IInvoiceOpenSalesRepRecord) => [
            item.saleRep?.name || '-',
            item.status || '-',
            item.number || '-',
            item.dueDate ? dayjs(item.dueDate).format('MM/DD/YYYY') : '-',
            item.dateInvoiced ? dayjs(item.dateInvoiced).format('MM/DD/YYYY') : '-',
            item.total || '0',
            item.totalPaid || '0',
            item.balance || '0',
            item.relatedContact?.name || '-',
            item.jobUniqueCode || '-',
          ]);
          filename = 'invoice-open-sales-rep.csv';
          break;
        }

        case 'paid-and-left-review': {
          const response = await reportApi.getPaidAndLeftReview({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = ['Sales Rep', 'Primary Contact', 'Address Line', 'City', 'State', 'Zip', 'Type', 'Status', 'Date Status Changes'];
          rows = records.map((item: IPaidAndLeftReviewRecord) => [
            item.saleRep?.name || '-',
            item.primaryContact?.name || '-',
            item.addressLine || '-',
            item.city || '-',
            item.state || '-',
            item.zip || '-',
            item.recordType || '-',
            item.status || '-',
            item.dateStatusChanges ? dayjs(item.dateStatusChanges).format('MM/DD/YYYY hh:mm A') : '-',
          ]);
          filename = 'paid-and-left-review.csv';
          break;
        }

        case 'payments': {
          const response = await reportApi.getPayments({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = ['Date Payment', 'Reference', 'Total', 'Invoice Code', 'Invoice Total', 'Method', 'Status', 'Related Contact', 'Job Code', 'Job Title', 'Created By', 'Date Updated'];
          rows = records.map((item: IPaymentRecord) => [
            item.datePayment ? dayjs(item.datePayment).format('MM/DD/YYYY') : '-',
            item.reference || '-',
            formatCurrency(item.total),
            item.invoice?.uniqueCode || '-',
            formatCurrency(item.invoice?.total),
            item.method || '-',
            item.status || '-',
            item.relatedContact?.name || '-',
            item.job?.uniqueCode || '-',
            item.job?.title || '-',
            item.createdByAccount?.name || '-',
            item.dateUpdated ? dayjs(item.dateUpdated).format('MM/DD/YYYY hh:mm A') : '-',
          ]);
          filename = 'payments.csv';
          break;
        }

        case 'my-payments': {
          const response = await reportApi.getMyPayments({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = ['Date Payment', 'Reference', 'Total', 'Invoice Code', 'Invoice Total', 'Method', 'Related Contact', 'Job Code', 'Created By', 'Date Updated'];
          rows = records.map((item: IMyPaymentsRecord) => [
            item.datePayment ? dayjs(item.datePayment).format('MM/DD/YYYY hh:mm A') : '-',
            item.paymentReference || '-',
            formatCurrency(item.total),
            item.invoice?.unicode || '-',
            formatCurrency(item.invoice?.total),
            item.method || '-',
            item.relatedContact?.name || '-',
            item.job?.uniqueCode || '-',
            item.createdBy?.name || '-',
            item.dateUpdated ? dayjs(item.dateUpdated).format('MM/DD/YYYY hh:mm A') : '-',
          ]);
          filename = 'my-payments.csv';
          break;
        }

        case 'roofs': {
          const response = await reportApi.getRoofs({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = ['Trade', 'Address Line', 'Zip', 'Shingle Manufacturer', 'Shingle Color', 'Sales Rep', 'Status', 'Date Status Changes', 'Type', 'Related Contact'];
          rows = records.map((item: IRoofs) => [
            item.trade || '-',
            item.addressLine || '-',
            item.zip || '-',
            item.shingleManufacturer || '-',
            item.shingleColor || '-',
            item.saleRep?.name || '-',
            item.status || '-',
            item.dateStatusChanges ? dayjs(item.dateStatusChanges).format('MM/DD/YYYY hh:mm A') : '-',
            item.recordType || '-',
            item.relatedContact?.name || '-',
          ]);
          filename = 'roofs.csv';
          break;
        }

        case 'shelbyville-job': {
          const response = await reportApi.getShelbyvilleJob({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = ['Name', 'Type', 'Status', 'Sales Rep', 'Primary Contact', 'Assigned To', 'Zip', 'Total Estimate', 'Date Created', 'Contract Signed Date'];
          rows = records.map((item: IShelbyvilleJob) => [
            item.relatedContact?.name || '-',
            item.type || '-',
            item.status || '-',
            item.saleRep?.name || '-',
            item.relatedContact?.name || '-',
            item.assignedTo?.name || '-',
            item.zip || '-',
            formatCurrency(item.totalEstimate),
            item.dateCreated ? dayjs(item.dateCreated).format('MM/DD/YYYY hh:mm A') : '-',
            item.signedDate ? dayjs(item.signedDate).format('MM/DD/YYYY') : '-',
          ]);
          filename = 'shelbyville-job.csv';
          break;
        }

        case 'shingle-color': {
          const response = await reportApi.getShingleColor({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = ['Name', 'Job Code', 'Status', 'Sales Rep', 'Primary Contact', 'Assigned To', 'Trade', 'Shingle Color', 'Shingle Manufacturer', 'Address Line', 'Zip'];
          rows = records.map((item: IShingleColor) => [
            item.relatedContact?.name || '-',
            item.job?.uniqueCode || '-',
            item.status || '-',
            item.saleRep?.name || '-',
            item.relatedContact?.name || '-',
            item.assignedTo?.name || '-',
            item.trade || '-',
            item.shingleColor || '-',
            item.shingleManufacturer || '-',
            item.addressLine || '-',
            item.zip || '-',
          ]);
          filename = 'shingle-color.csv';
          break;
        }

        case 'shingle': {
          const response = await reportApi.getShingle({ page: 1 });
          const records = response.data?.data?.responses ?? [];
          header = ['Name', 'Job Code', 'Trade', 'Sales Rep', 'Primary Contact', 'Assigned To', 'Shingle Manufacturer', 'Shingle Color', 'Address Line', 'Zip', 'Status', 'Type'];
          rows = records.map((item: IShingle) => [
            item.relatedContact?.name || '-',
            item.job?.uniqueCode || '-',
            item.trade || '-',
            item.saleRep?.name || '-',
            item.relatedContact?.name || '-',
            item.assignedTo?.name || '-',
            item.shingleManufacturer || '-',
            item.shingleColor || '-',
            item.addressLine || '-',
            item.zip || '-',
            item.status || '-',
            item.type || '-',
          ]);
          filename = 'shingle.csv';
          break;
        }

        default:
          throw new Error(`Unknown report type: ${selectedReportType}`);
      }

      const csvContent = [header, ...rows]
        .map((row) => row.map((cell) => escapeCsvValue(String(cell ?? ''))).join(','))
        .join('\n');

      const blob = new Blob([`\uFEFF${csvContent}`], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setIsExportModalOpen(false);
    } catch (error) {
      console.error('Failed to export report', error);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, selectedReportType]);

  return (
    <Box className={cx('report-container')}>
      <Box className={cx('report-root')}>
        <Box className={cx('header')}>
          <Typography className={cx('title')}>Reports</Typography>
          <Typography className={cx('subtitle')}>Comprehensive insights into your business performance</Typography>
        </Box>
        <Box className={cx('top-actions')}>
          <Box className={cx('left-actions')}>
            <PrimaryButton
              title='Insights'
              onClick={() => handleChangeView('insights')}
              className={cx('view-tab', view === 'insights' && 'active')}
            />
            <PrimaryButton
              title='Classic Reports'
              onClick={() => handleChangeView('classic')}
              className={cx('view-tab', view === 'classic' && 'active')}
            />
          </Box>

          {view === 'classic' && (
            <Box className={cx('right-actions')}>
              <PrimaryButton
                title='Export'
                icon={<ExportFileIcon />}
                onClick={handleOpenExport}
                className={cx('export')}
              />
            </Box>
          )}
        </Box>

        {view === 'insights' ? (
          <>
            <ReportStats insights={insights} />
            <Box className={cx('trend-projects-row')}>
              <Box className={cx('trend-col')}>
                <DealOverviewChart className={cx('deal-overview-chart')} classTitle={cx('deal-overview-chart-title')} />
              </Box>
              <Box className={cx('projects-col')}>
                <RecentProjects insights={insights} />
              </Box>
            </Box>
          </>
        ) : (
          <Box className={cx('classic-wrapper')}>
            {CLASSIC_REPORTS.map((report) => (
              <div className={cx('classic-card')} onClick={() => navigate(`${adminRouteAbsolute.reportClassicDetail(report.path)}`)}>
                <Typography variant='subtitle1' className={cx('classic-title')}>
                  {report.title}
                </Typography>
                <Typography variant='body2' className={cx('classic-meta')}>
                  <span className={cx('meta-value')}>Type: {report.type}</span>
                </Typography>
                <Typography variant='body2' className={cx('classic-body')}>
                  {report.description}
                </Typography>
              </div>
            ))}
          </Box>
        )}

        <BaseModal open={isExportModalOpen} onClose={handleCloseExport}>
          <Box className={cx('export-modal')}>
            <Typography className={cx('export-modal__title')}>Export reports</Typography>
            <Typography className={cx('export-modal__description')}>
              Select the report dataset you want to download as a CSV file.
            </Typography>
            <Box className={cx('export-modal__options')}>
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  '.MuiInputLabel-root': {
                    color: '#C4CCE0',
                    '&.Mui-focused': { color: '#F9FBFE' },
                  },
                  '.MuiInputLabel-root.Mui-focused': {
                    color: '#F9FBFE',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(89, 124, 173, 0.35)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#4C96FF',
                  },
                  '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2D80FC',
                  },
                }}
              >
                <InputLabel id="report-type-select-label">Report Type</InputLabel>
                <Select
                  labelId="report-type-select-label"
                  value={selectedReportType}
                  label="Report Type"
                  onChange={(event) => setSelectedReportType(event.target.value)}
                  sx={{
                    color: '#F9FBFE',
                    '.MuiSelect-select': {
                      color: '#F9FBFE',
                      '&.Mui-disabled': {
                        color: 'rgba(249, 251, 254, 0.6)',
                      },
                    },
                    '.MuiSelect-icon': { color: '#F9FBFE' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2D80FC',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#0c1a31',
                        color: '#F9FBFE',
                        '& .MuiMenuItem-root.Mui-selected': {
                          bgcolor: 'rgba(45,128,252,0.25)',
                        },
                        '& .MuiMenuItem-root:hover': {
                          bgcolor: 'rgba(45,128,252,0.35)',
                        },
                      },
                    },
                  }}
                >
                  {CLASSIC_REPORTS.map((report) => (
                    <MenuItem key={report.path} value={report.path}>
                      {report.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className={cx('export-modal__actions')}>
              <Button variant="text" onClick={handleCloseExport}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirmExport}
                disabled={!selectedReportType || isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </Box>
            <Typography className={cx('export-modal__footer')}>
              Report selected: {CLASSIC_REPORTS.find((report) => report.path === selectedReportType)?.title ?? 'N/A'}
            </Typography>
          </Box>
        </BaseModal>
      </Box>
    </Box>
  );
};

export default Report;
